import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrgAdminLayout from 'screens/organizations/components/OrgAdminLayout';
import PageLayout from 'components/PageLayout';
import { useGetGroup, useGetGroupAlbums } from 'legacy/groups/hooks';
import { useRouter } from 'next/router';
import { MdPermMedia } from 'react-icons/md';
import ThumbnailList from 'components/Moments/ThumbnailList';
import classnames from 'classnames';
import styles from './style.module.scss';
import AlbumForm from 'components/Moments/AlbumForm';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import PaginationControls from 'components/PaginationControls';
import MomentsOrganizationHeader from './MomentsOrganizationHeader';
import AlbumActions from './AlbumActions';
import AlbumActionsModal from './AlbumActionsModal';
import { AlbumDto } from 'legacy/groups/types';
import DeleteAlbumModal from 'components/Moments/DeleteAlbumModal';

const OrganizationMoments = () => {
  const { t } = useTranslation(['organizations']);
  const [isNewAlbumModalOpen, setIsNewAlbumModalOpen] = useState(false);
  const [isAlbumActionsModalOpen, setIsAlbumActionsModalOpen] = useState(false);
  const [isAlbumEditModalOpen, setIsAlbumEditModalOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumDto>();
  const [isDeleteAlbumModalOpen, setIsDeleteAlbumModalOpen] = useState(false);
  const router = useRouter();

  const groupId = router.query.organizationId as string;

  const {
    data: albums,
    isLoading: isLoadingAlbums,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage,
    refreshAlbums
  } = useGetGroupAlbums({
    groupId
  });

  const { data: organization } = useGetGroup({
    groupId
  });

  const redirectToMoments = (albumId: string, albumName: string) => {
    router.push(
      `/organizations/${groupId}/moments/albums/${albumId}?albumName=${albumName}`
    );
  };

  const renderAlbumsContent = () => {
    if (isLoadingAlbums) {
      return (
        <p className="mt-12">
          {t('moments.loading', { item: t('moments.albums').toLowerCase() })}
        </p>
      );
    }

    if (albums?.length > 0) {
      return (
        <div className={classnames('grid gap-4', styles['albums-grid'])}>
          {albums.map((album) => (
            <div className="mt-5" key={`${album.id}-album`}>
              <ThumbnailList
                images={album.momentsPreview.map((moment) =>
                  moment.media[0].type === 'video'
                    ? moment.media[0].thumbnail
                    : moment.media[0].url
                )}
                onClick={() => {
                  redirectToMoments(album.id, album.name);
                }}
                onClickFirstThumbnail={() => {
                  router.push(
                    `/organizations/${groupId}/moments/albums/${album.id}/create`
                  );
                }}
              />
              <div className="flex items-center justify-between mt-3">
                <h2
                  onClick={() => {
                    redirectToMoments(album.id, album.name);
                  }}
                  className={classnames(
                    'cursor-pointer text-lg font-semibold',
                    styles['album-link']
                  )}
                >
                  {album.name}
                </h2>
                <AlbumActions
                  onClick={() => {
                    setIsAlbumActionsModalOpen(true);
                    setSelectedAlbum(album);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <p className="mt-12">
        {t('moments.noItemsFound', { item: t('moments.albums').toLowerCase() })}
      </p>
    );
  };

  return (
    <OrgAdminLayout>
      <PageLayout
        isAdmin={organization?.isUserAdmin}
        isUserMember={organization?.isUserMember}
      >
        <div className="flex flex-col mt-10">
          <MomentsOrganizationHeader
            onCreateAlbumClick={() => setIsNewAlbumModalOpen(true)}
            title={t('moments.albums')}
            icon={<MdPermMedia />}
            linkText={t('moments.createAlbum')}
          />
          {(hasNextPage || hasPreviousPage) && (
            <div className="flex justify-end mt-4">
              <PaginationControls
                onPrevious={fetchPreviousPage}
                onNext={fetchNextPage}
                hasPrevious={hasPreviousPage}
                hasNext={hasNextPage}
              />
            </div>
          )}
          {renderAlbumsContent()}
        </div>
      </PageLayout>

      {isNewAlbumModalOpen && (
        <Modal
          type={ModalType.SMALL}
          onClose={() => setIsNewAlbumModalOpen(false)}
          autoHeight
          hideCloseButton
          visible={isNewAlbumModalOpen}
        >
          <AlbumForm
            groupId={groupId}
            onComplete={() => {
              setIsNewAlbumModalOpen(false);
              refreshAlbums();
            }}
          />
        </Modal>
      )}
      {isAlbumActionsModalOpen && (
        <Modal
          type={ModalType.SMALL}
          onClose={() => setIsAlbumActionsModalOpen(false)}
          autoHeight
          hideCloseButton
          visible={isAlbumActionsModalOpen}
        >
          <AlbumActionsModal
            canDelete={selectedAlbum?.canDelete}
            isAdmin={organization?.isUserAdmin}
            onEditAlbum={() => {
              setIsAlbumEditModalOpen(true);
              setIsAlbumActionsModalOpen(false);
            }}
            onViewAlbum={() =>
              redirectToMoments(selectedAlbum?.id, selectedAlbum?.name)
            }
            onDeleteAlbum={() => {
              setIsDeleteAlbumModalOpen(true);
            }}
            shareLink={`/organizations/${groupId}/moments/albums/${selectedAlbum?.id}?albumName=${selectedAlbum?.name}`}
          />
        </Modal>
      )}

      {isAlbumEditModalOpen && (
        <Modal
          type={ModalType.SMALL}
          onClose={() => setIsAlbumEditModalOpen(false)}
          autoHeight
          hideCloseButton
          visible={isAlbumEditModalOpen}
        >
          <AlbumForm
            groupId={groupId}
            album={selectedAlbum}
            onDeleteAlbum={() => {
              setIsDeleteAlbumModalOpen(true);
            }}
            onComplete={() => {
              setIsAlbumEditModalOpen(false);
              refreshAlbums();
            }}
          />
        </Modal>
      )}

      {isDeleteAlbumModalOpen && (
        <Modal
          type={ModalType.SMALL}
          onClose={() => setIsDeleteAlbumModalOpen(false)}
          autoHeight
          hideCloseButton
          visible={isDeleteAlbumModalOpen}
        >
          <DeleteAlbumModal
            groupId={groupId}
            album={selectedAlbum}
            handleCancel={() => setIsDeleteAlbumModalOpen(false)}
            onComplete={() => {
              setIsDeleteAlbumModalOpen(false);
              setIsAlbumActionsModalOpen(false);
              setIsAlbumEditModalOpen(false);
              refreshAlbums();
            }}
          />
        </Modal>
      )}
    </OrgAdminLayout>
  );
};

export default OrganizationMoments;
